import type { Feature } from "@unbox/db";
import { ArrowUpRight, Folder } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { useSafetyModelCount } from "@/hooks/use-safety-model-count";
import { useSort } from "@/hooks/use-sort";
import { TabHeaderReadOnly } from "@/pages/admin/merchant-detail/tabs/tab-header";
import { RemoveFromWorkspaceButton } from "@/pages/admin/modals/remove-from-workspace-button";
import { SelectBrandsForWorkspaceModal } from "@/pages/admin/modals/select-brands-for-workspace-modal";
import { FEATURE_LABELS } from "@/utils/features";

const featuresColor = {
  api: "blue-light",
  assurance: "purple",
  insights: "orange",
  explorer: "success",
  reviewQueue: "brand",
  veryLowVolume: "blue",
  hideExplorers: "gray",
  noAffiliateAuthorisation: "warning",
  pastDueBanner: "error",
} as const;

const SafetyModelsCell = ({ merchantId }: { merchantId: string }) => {
  const { total, hasCustom } = useSafetyModelCount(merchantId);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-primary">{total}</span>
      {hasCustom && <Badge color="gray-blue">Custom</Badge>}
    </div>
  );
};

type BrandsTabProps = {
  workspace: {
    id: string;
    name: string;
    merchants: Array<{
      id: string;
      name: string;
      logoUrl?: string | null;
      features?: Feature[] | null;
    }>;
  };
};

const PAGE_SIZE = 20;

export const BrandsTab = ({ workspace }: BrandsTabProps) => {
  const [addBrandsOpen, setAddBrandsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const sort = useSort<"name">("name");

  const sortedMerchants = useMemo(() => {
    return [...workspace.merchants].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return sort.sort.direction === "asc" ? cmp : -cmp;
    });
  }, [workspace.merchants, sort.sort.direction]);

  const totalPages = Math.ceil(sortedMerchants.length / PAGE_SIZE);
  const paginatedMerchants = sortedMerchants.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex flex-col gap-6 h-full">
      <TabHeaderReadOnly
        title="Merchants"
        subtitle="This is a list of all merchants accessible through this workspace"
        rightContent={
          <Button color="primary" onClick={() => setAddBrandsOpen(true)}>
            Add merchant to Workspace
          </Button>
        }
      />

      <div className="border-t border-primary" />

      {workspace.merchants.length > 0 ? (
        <>
          <Table
            aria-label="Workspace merchants"
            sortDescriptor={sort.descriptor}
            onSortChange={sort.setFromDescriptor}
          >
            <Table.Header className="bg-transparent">
              <Table.Head id="name" label="Name" isRowHeader allowsSorting className="w-[45%]" />
              <Table.Head id="permissions" label="Permissions" className="w-[25%]" />
              <Table.Head id="safety" label="Safety Models" className="w-[20%]" />
              <Table.Head id="actions" label="" className="w-[10%]" />
            </Table.Header>

            <Table.Body items={paginatedMerchants}>
              {(item) => (
                <Table.Row
                  id={item.id}
                  className="even:bg-secondary cursor-pointer"
                  onClick={() => window.open(`/admin/merchants/${item.id}`, "_blank")}
                >
                  <Table.Cell className="whitespace-nowrap p-0">
                    <div className="w-full h-full flex items-center gap-3 px-4 py-3">
                      <MerchantLogo name={item.name} logoUrl={item.logoUrl} size="sm" />
                      <div className="flex flex-col text-left">
                        <p className="text-sm font-medium text-primary hover:underline">{item.name}</p>
                        <p className="text-sm text-tertiary">Merchant ID: {item.id}</p>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex flex-row flex-wrap gap-1">
                      {(item.features ?? []).map((feature) => (
                        <Badge color={featuresColor[feature as keyof typeof featuresColor] ?? "gray"} key={feature}>
                          {FEATURE_LABELS[feature]}
                        </Badge>
                      ))}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <SafetyModelsCell merchantId={item.id} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/admin/merchants/${item.id}`, "_blank");
                        }}
                        className="p-1.5 rounded-md text-tertiary hover:text-primary hover:bg-secondary transition-colors"
                        aria-label={`View ${item.name}`}
                      >
                        <ArrowUpRight className="size-4" />
                      </button>
                      <RemoveFromWorkspaceButton
                        workspaceId={workspace.id}
                        merchantId={item.id}
                        name={item.name}
                        removing="brand"
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <PaginationPageMinimalCenter
              page={page}
              total={totalPages}
              onPageChange={setPage}
              className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
            />
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 flex-1">
          <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
            <Folder className="size-6 text-secondary" />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-semibold text-primary">No Merchants in workspace</p>
            <p className="text-sm text-tertiary max-w-xs">
              Add a merchant to a workspace to give people access. Anyone in that workspace will be able to view it.
            </p>
          </div>
        </div>
      )}

      <SelectBrandsForWorkspaceModal workspaceId={workspace.id} open={addBrandsOpen} onOpenChange={setAddBrandsOpen} />
    </div>
  );
};
